/**
 * Pretty-text renderers for `canary workflow proposals list | diff`.
 * Pure — no fetching, easy to test.
 */

import { getNodeDisplayTitle } from '@chatsdet/workflow-diff';
import type { ProposalSummary, DiffPayload } from './workflow-proposals.js';

const STATUS_DOTS: Record<string, string> = {
  pending: '●',
  accepted: '✓',
  rejected: '✗',
  stale: '⚠',
};

export function renderProposalList(proposals: ProposalSummary[]): string {
  if (proposals.length === 0) return 'No proposals.';
  const rows = proposals.map((p) => {
    const dot = STATUS_DOTS[p.status] ?? '•';
    return `${dot} ${pad(p.status, 10)} ${pad(p.source, 8)} ${p.id.slice(0, 8)}…  ${p.title}`;
  });
  return [
    `${proposals.length} proposal${proposals.length === 1 ? '' : 's'}`,
    '',
    ...rows,
  ].join('\n');
}

export function renderProposalDiff(
  payload: DiffPayload,
  opts: { includeUnchanged?: boolean } = {},
): string {
  const { proposal, summary, nodes, edges } = payload;
  const staleNote = proposal.stale ? '  ⚠ STALE' : '';
  const header = [
    `Proposal: ${proposal.title}`,
    `Source: ${proposal.source}   Status: ${proposal.status}${staleNote}`,
    `Created: ${proposal.createdAt}`,
    '',
    `Summary: ${summary.addedNodes} added · ${summary.removedNodes} removed · ${summary.changedNodes} changed · ${summary.touchedCount} touched`,
  ];

  if (summary.isEmpty) {
    return [...header, '', 'No changes — proposal matches the current draft.'].join('\n');
  }

  const blocks: string[] = [];

  for (const n of nodes.added) {
    blocks.push(
      `+ ${pad(n.nodeType.toUpperCase(), 14)} ${getNodeDisplayTitle(n.nodeType, n.configJson)}   (${n.id})`,
    );
  }
  for (const n of nodes.removed) {
    blocks.push(
      `- ${pad(n.nodeType.toUpperCase(), 14)} ${getNodeDisplayTitle(n.nodeType, n.configJson)}   (${n.id})`,
    );
  }
  for (const c of nodes.changed) {
    const lines = [
      `~ ${pad(c.nodeType.toUpperCase(), 14)} ${c.id}`,
    ];
    if (c.typeChanged) {
      lines.push(`    nodeType: ${c.beforeType} → ${c.afterType}`);
    }
    for (const fc of c.fieldChanges) {
      lines.push(`    ${fc.path}: ${fmt(fc.before)} → ${fmt(fc.after)}`);
    }
    blocks.push(lines.join('\n'));
  }

  const edgeLine = `Edges: +${summary.addedEdges}  -${summary.removedEdges}`;
  if (edges.added.length === 0 && edges.removed.length === 0) {
    return [...header, '', ...blocks, '', edgeLine].join('\n');
  }
  const edgeLines = [
    ...edges.added.map(
      (e) => `  + ${e.sourceNodeId} → ${e.targetNodeId}${e.edgeType ? ` [${e.edgeType}]` : ''}`,
    ),
    ...edges.removed.map(
      (e) => `  - ${e.sourceNodeId} → ${e.targetNodeId}${e.edgeType ? ` [${e.edgeType}]` : ''}`,
    ),
  ];

  const out = [...header, '', ...blocks, '', edgeLine, ...edgeLines];

  // --include-unchanged is a passthrough hook for a future expansion; today
  // the diff engine doesn't surface unchanged nodes, so we just note that.
  if (opts.includeUnchanged) {
    out.push('', '(--include-unchanged has no additional output in v1)');
  }

  return out.join('\n');
}

function pad(s: string, n: number): string {
  return s.length >= n ? s : s + ' '.repeat(n - s.length);
}

function fmt(v: unknown): string {
  if (v === undefined) return '(none)';
  if (v === null) return 'null';
  if (typeof v === 'string') return JSON.stringify(v);
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
